import {
  ALERT_ACTION_LABELS,
  AcceptDeviationRequestSchema,
  CHANGE_ORDER_STATUS_LABELS,
  CloseAlertRequestSchema,
  SEVERITY_UI,
  ageLabel,
  alertHeading,
  alertStateUi,
  closedLine,
  daysUntil,
  formatStamp,
  waitingHeadline,
} from '@baseline/shared';
import { useQueryClient } from '@tanstack/react-query';
import { useEffect, useRef, useState } from 'react';
import { Link, useNavigate, useParams, useSearchParams } from 'react-router';

import { ApiError, api } from '../api/client.js';
import {
  filterQuery,
  useAlert,
  useAlertAction,
  useAlertContext,
  useDraft,
  useInvalidateAlerts,
} from '../api/queries.js';
import { readFilter } from '../app/filters.js';
import { Card } from '../components/Card.js';
import { resetCollapsed, useCollapsed } from '../components/collapse.js';
import { CardChevron } from '../components/icons.js';
import { PageError } from '../components/PageStatus.js';
import { SeverityPill, StatusPill } from '../components/Pills.js';
import { useToast } from '../components/Toast.js';

import type { AlertAction, AlertDetail, DraftResponse, TimelineEntry } from '@baseline/shared';
import type { ZodType } from 'zod';

// Prototype `vDetail` (plan Phase 11). The recommendation card follows the alert's state;
// which buttons appear comes from `allowedActions`, so the UI holds no rules.

function errorText(err: unknown): string {
  return err instanceof ApiError ? err.message : 'Baseline couldn’t reach the server. Try again.';
}

/** The reason box for accepting a deviation or closing after a timeout. */
function ReasonBox(props: {
  id: string;
  label: string;
  placeholder: string;
  confirm: string;
  schema: ZodType;
  busy: boolean;
  onConfirm: (reason: string) => void;
  onCancel: () => void;
}) {
  const [reason, setReason] = useState('');
  const [err, setErr] = useState('');
  const ref = useRef<HTMLTextAreaElement>(null);
  useEffect(() => ref.current?.focus(), []);
  const submit = () => {
    const parsed = props.schema.safeParse({ expectedVersion: 0, reason });
    if (!parsed.success) {
      setErr(parsed.error.issues.find((i) => i.path[0] === 'reason')?.message ?? '');
      ref.current?.focus();
      return;
    }
    props.onConfirm(reason.trim());
  };
  return (
    <div className="devbox">
      <label htmlFor={props.id}>{props.label}</label>
      <textarea
        ref={ref}
        id={props.id}
        rows={3}
        placeholder={props.placeholder}
        value={reason}
        aria-invalid={err ? true : undefined}
        aria-describedby={`${props.id}-err`}
        onChange={(e) => setReason(e.target.value)}
      />
      <div className="err" id={`${props.id}-err`} role="alert">
        {err}
      </div>
      <div className="btn-row">
        <button className="btn btn-p" type="button" disabled={props.busy} onClick={submit}>
          {props.confirm}
        </button>
        <button className="btn" type="button" onClick={props.onCancel}>
          Cancel
        </button>
      </div>
    </div>
  );
}

/** Draft ready: review, edit and approve. An edited body is saved as a new version first. */
function DraftEditor({ alert }: { alert: AlertDetail }) {
  const { data, error } = useDraft(alert.draftId);
  const invalidate = useInvalidateAlerts();
  const qc = useQueryClient();
  const toast = useToast();
  const [body, setBody] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');
  const draft = data?.draft;
  const versionId = draft?.currentVersion.id;

  // A new version from elsewhere replaces what is in the editor.
  useEffect(() => setBody(null), [versionId]);

  if (error) return <div className="err">{errorText(error)}</div>;
  if (!draft) return null;
  const text = body ?? draft.currentVersion.body;

  const run = async (fn: () => Promise<void>) => {
    setBusy(true);
    setErr('');
    try {
      await fn();
    } catch (e) {
      setErr(errorText(e));
    } finally {
      setBusy(false);
      await invalidate();
    }
  };

  const approve = () =>
    run(async () => {
      let version = draft.currentVersion.id;
      if (text !== draft.currentVersion.body) {
        const saved = await api<DraftResponse>(`/drafts/${draft.id}`, {
          method: 'PUT',
          body: { baseVersionId: version, body: text },
        });
        qc.setQueryData(['drafts', draft.id], saved);
        version = saved.draft.currentVersion.id;
      }
      // BR-030: the approval names exactly this version.
      await api<DraftResponse>(`/drafts/${draft.id}/approve`, {
        method: 'POST',
        body: { versionId: version },
      });
      toast(`Email sent to ${draft.to}.`);
    });

  const discard = () =>
    run(async () => {
      await api<DraftResponse>(`/drafts/${draft.id}/discard`, { method: 'POST' });
    });

  return (
    <>
      <p className="rec">
        Review the email. <b>Nothing is sent until you approve it.</b>
      </p>
      <div className="mail">
        <div className="mrow">
          <span>To</span>
          {draft.to}
        </div>
        <div className="mrow">
          <span>From</span>
          {draft.from}
        </div>
        <div className="mrow">
          <span>Subject</span>
          <b>{draft.currentVersion.subject}</b>
        </div>
        <textarea
          id="mail-body"
          rows={9}
          aria-label="Email message"
          value={text}
          onChange={(e) => setBody(e.target.value)}
        />
        <div className="mfoot">
          <button className="btn" type="button" disabled={busy} onClick={() => void discard()}>
            Discard
          </button>
          <button
            className="btn btn-p"
            type="button"
            disabled={busy}
            onClick={() => void approve()}
          >
            Approve and send
          </button>
        </div>
      </div>
      {draft.lastError ? (
        <div className="err act-err" role="alert">
          The email didn’t send: {draft.lastError}
        </div>
      ) : null}
      {err ? (
        <div className="err act-err" role="alert">
          {err}
        </div>
      ) : null}
    </>
  );
}

/** New and Needs decision: the recommendation plus the other actions the caller may take. */
function Decide({ alert }: { alert: AlertDetail }) {
  const action = useAlertAction(alert);
  const toast = useToast();
  const [box, setBox] = useState<'deviation' | 'close' | null>(null);
  const [err, setErr] = useState('');
  const allowed = new Set(alert.allowedActions);
  const deciding = alert.state === 'needs_human_decision';

  const act = (a: AlertAction, body?: Record<string, unknown>, done?: (r: AlertDetail) => void) => {
    setErr('');
    action.mutate(
      { action: a, ...(body ? { body } : {}) },
      {
        onSuccess: (res) => {
          setBox(null);
          done?.(res.alert);
        },
        onError: (e) => setErr(errorText(e)),
      },
    );
  };

  // Past a timeout, spec §4 offers close, follow up, escalate and accept deviation.
  const extra: AlertAction[] = deciding ? ['recheck', 'escalate', 'close'] : [];

  return (
    <>
      <p className="rec">{alert.recommendation.text}</p>
      <div className="btn-row">
        {allowed.has('accept-recommendation') ? (
          <button
            className="btn btn-p"
            type="button"
            disabled={action.isPending}
            onClick={() =>
              act('accept-recommendation', undefined, (a) => {
                if (a.openRequest) toast(`Request sent to ${a.openRequest.requestedName}.`);
              })
            }
          >
            {alert.recommendation.label}
          </button>
        ) : null}
        {allowed.has('accept-deviation') ? (
          <button className="btn" type="button" onClick={() => setBox('deviation')}>
            {ALERT_ACTION_LABELS['accept-deviation']}
          </button>
        ) : null}
        {extra
          .filter((a) => allowed.has(a))
          .map((a) => (
            <button
              key={a}
              className="btn"
              type="button"
              disabled={action.isPending}
              onClick={() => (a === 'close' ? setBox('close') : act(a))}
            >
              {ALERT_ACTION_LABELS[a]}
            </button>
          ))}
      </div>
      {box === 'deviation' ? (
        <ReasonBox
          id="dev-reason"
          label="Why is this difference intentional?"
          placeholder="For example: building with the old quantity until the next release."
          confirm="Accept difference"
          schema={AcceptDeviationRequestSchema as ZodType}
          busy={action.isPending}
          onCancel={() => setBox(null)}
          onConfirm={(reason) =>
            act('accept-deviation', { reason }, () => toast('Accepted as intentional.'))
          }
        />
      ) : null}
      {box === 'close' ? (
        <ReasonBox
          id="close-reason"
          label="Why are you closing this alert?"
          placeholder="For example: the supplier confirmed by phone and the ERP will follow."
          confirm="Close alert"
          schema={CloseAlertRequestSchema as ZodType}
          busy={action.isPending}
          onCancel={() => setBox(null)}
          onConfirm={(reason) => act('close', { reason })}
        />
      ) : null}
      {err ? (
        <div className="err act-err" role="alert">
          {err}
        </div>
      ) : null}
    </>
  );
}

function Waiting({ bold, hint }: { bold: string; hint: string }) {
  return (
    <div className="wait">
      <span className="pulse" />
      <div>
        <b style={{ color: 'var(--ink)' }}>{bold}</b>
        <div className="hint">{hint}</div>
      </div>
    </div>
  );
}

function RecommendationBody({ alert }: { alert: AlertDetail }) {
  const po = alert.purchaseOrder;
  const lastReply = [...alert.timeline].reverse().find((e) => e.actorType === 'external');
  let body;
  switch (alert.state) {
    case 'triaged':
    case 'reopened':
    case 'needs_human_decision':
      body = <Decide alert={alert} />;
      break;
    case 'draft_ready':
      body = <DraftEditor alert={alert} />;
      break;
    case 'awaiting_others':
      body = (
        <Waiting
          bold={waitingHeadline(alert.openRequest)}
          hint="Baseline checks automatically. Only a re-read of PDM and ERP closes an alert."
        />
      );
      break;
    case 'awaiting_verification':
      body = (
        <Waiting
          bold="Reply received. Verifying…"
          hint={`${lastReply ? `${lastReply.text} ` : ''}Re-reading PDM and ERP.`}
        />
      );
      break;
    default: {
      const line = closedLine(alert.state, alert.closedReason ?? alert.deviationReason);
      body = (
        <div className="okline">
          <b>{line.bold}</b> {line.rest}
        </div>
      );
    }
  }
  return (
    <>
      {body}
      <div className="evid">
        <span>
          <em>Diagnosis</em>
          {alert.diagnosisLabel}
        </span>
        <span>
          <em>Change order</em>
          {alert.changeOrder
            ? `${alert.changeOrder.code} · ${CHANGE_ORDER_STATUS_LABELS[alert.changeOrder.status]}`
            : 'None found'}
        </span>
        <span>
          <em>Purchase order</em>
          {po ? `${po.code} · ${String(daysUntil(po.releaseDate))} days` : 'None soon'}
        </span>
      </div>
      <details className="whyd">
        <summary>Why {SEVERITY_UI[alert.severity].label.toLowerCase()} severity</summary>
        <ul>
          {alert.severityRules.map((r) => (
            <li key={r.ruleId}>{r.text}</li>
          ))}
        </ul>
      </details>
    </>
  );
}

function avatarClass(e: TimelineEntry): string {
  if (e.actorType === 'agent') return 'agent';
  if (e.actorType === 'user') return 'you';
  return 'other';
}

function Timeline({ entries }: { entries: TimelineEntry[] }) {
  return (
    <ol className="act">
      {entries.map((e) => (
        <li key={e.id}>
          <span className={`av2 av2-${avatarClass(e)}`}>{e.avatar}</span>
          <div>
            <div className="at">{e.text}</div>
            {e.rationale ? <div className="why">{e.rationale}</div> : null}
            <div className="ad">{formatStamp(e.at)}</div>
          </div>
        </li>
      ))}
    </ol>
  );
}

function BomContext({ alert }: { alert: AlertDetail }) {
  const [open, toggle] = useCollapsed('det:bom');
  const { data } = useAlertContext(alert.id);
  const navigate = useNavigate();
  const compare = () => {
    const p = new URLSearchParams({ assemblyId: alert.assemblyId });
    if (alert.pdmRev) p.set('aRev', alert.pdmRev);
    if (alert.erpRev) p.set('bRev', alert.erpRev);
    void navigate(`/compare?${p.toString()}`);
  };
  return (
    <section className="card">
      <div
        className="card-h ffh"
        role="button"
        tabIndex={0}
        aria-expanded={open}
        onClick={toggle}
        onKeyDown={(e) => {
          if (e.target === e.currentTarget && (e.key === 'Enter' || e.key === ' ')) {
            e.preventDefault();
            toggle();
          }
        }}
      >
        <span className="bk">Where it sits in the BOM</span>
        <span className="hdr-acts">
          <button
            className="lnk"
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              compare();
            }}
          >
            Open full comparison
          </button>
          <CardChevron open={open} />
        </span>
      </div>
      {open ? (
        <div style={{ marginTop: 12 }}>
          <table className="ctx">
            <thead>
              <tr>
                <th>Part</th>
                <th className="num">
                  <i className="mkA" />
                  Design
                </th>
                <th className="num">
                  <i className="mkB" />
                  ERP
                </th>
              </tr>
            </thead>
            <tbody>
              {(data?.rows ?? []).map((r) => (
                <tr
                  key={`${String(r.depth)}:${r.pn}`}
                  className={`${r.isThisAlert ? 'focus' : ''}${r.isAncestor ? ' head' : ''}`}
                >
                  <td>
                    <div style={{ paddingLeft: r.depth * 18 }}>
                      <span className="mono-p">{r.pn}</span> {r.description}
                      {r.isThisAlert ? (
                        <>
                          {' '}
                          <span className="tg">this alert</span>
                        </>
                      ) : null}
                    </div>
                  </td>
                  <td className="num">{r.pdmQty ?? '—'}</td>
                  <td className="num">{r.erpQty ?? '—'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : null}
    </section>
  );
}

export default function AlertDetailPage() {
  const { id = '' } = useParams();
  const [params] = useSearchParams();
  const filter = readFilter(params);
  const query = filterQuery(filter);
  const navigate = useNavigate();
  const { data: alert, error } = useAlert(id, filter);

  // A different alert opens every section again, like the prototype's `go()`.
  useEffect(() => resetCollapsed('det:'), [id]);

  // Esc goes back to the list unless the user is typing.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      const el = document.activeElement;
      if (e.key !== 'Escape' || document.getElementById('pal-q')) return;
      if (el && /INPUT|TEXTAREA|SELECT/.test(el.tagName)) return;
      void navigate(`/alerts${query}`);
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [navigate, query]);

  if (error) return <PageError error={error} />;
  if (!alert) return <div className="pg" aria-busy="true" />;

  const open = alertStateUi(alert.state).view !== 'closed';
  const { position } = alert;
  const step = (to: string | null) => {
    if (to) void navigate(`/alerts/${to}${query}`);
  };

  return (
    <div className="pg">
      <div className="dtop">
        <div className="crumb">
          <Link className="bc-l" to={`/alerts${query}`}>
            Alerts
          </Link>
          <span className="bc-s">/</span>
          <span className="mono-s">{alert.code}</span>
        </div>
        <div className="step">
          <span className="hint">
            {position.total ? `${String(position.index + 1)} of ${String(position.total)}` : ''}
          </span>
          <button
            className="ib"
            type="button"
            aria-label="Previous alert"
            disabled={!position.prevId}
            onClick={() => step(position.prevId)}
          >
            ‹
          </button>
          <button
            className="ib"
            type="button"
            aria-label="Next alert"
            disabled={!position.nextId}
            onClick={() => step(position.nextId)}
          >
            ›
          </button>
        </div>
      </div>
      <div className="dhead">
        <h1>
          {alertHeading(alert.title, alert.detection)} <StatusPill state={alert.state} />
        </h1>
      </div>
      <div className="strip">
        <div>
          <div className="sl">
            <i className="mkA" />
            Design qty (PDM Rev {alert.pdmRev ?? '—'})
          </div>
          <div className="sv">{alert.pdmQty ?? 'None'}</div>
        </div>
        <div>
          <div className="sl">
            <i className="mkB" />
            ERP qty (Rev {alert.erpRev ?? '—'})
          </div>
          <div className={`sv ${open ? 'bad' : 'ok'}`}>{alert.erpQty ?? 'None'}</div>
        </div>
        <div>
          <div className="sl">Severity</div>
          <div className="sv t">
            <SeverityPill level={alert.severity} />
          </div>
        </div>
        <div>
          <div className="sl">Assembly</div>
          <div className="sv t">{alert.assemblyName}</div>
        </div>
        <div>
          <div className="sl">Age</div>
          <div className="sv t">{ageLabel(alert.openedAt)}</div>
        </div>
      </div>
      <div className="dcols2">
        <div className="dcol">
          <Card stateKey="det:rec" title="Recommended next step" titleStyle="bk">
            <RecommendationBody alert={alert} />
          </Card>
          <Card stateKey="det:tl" title="Timeline" titleStyle="bk">
            <Timeline entries={alert.timeline} />
          </Card>
        </div>
        <div className="dcol">
          <BomContext alert={alert} />
        </div>
      </div>
    </div>
  );
}
